<template>
    <div ref="assetList" class="asset-list" :style="{'gap': gapSize + 'px'}">

        <div v-if="!limitedList || limitedList.length === 0" class="asset-list-empty">
            The list is empty.
        </div>

        <div 
            v-else 
            v-for="(asset, index) in limitedList" 
            :key="asset.id"
            class="asset-list-item" 
            :style="{
                'width': itemSize + 'px',
                'max-width': itemSize + 'px',
                'height': itemSize + 'px',
                'max-height': itemSize + 'px'
            }">

            <AssetItem 
                ref="assetItems"
                ref_for 
                :asset="asset"  
                :file="file"
                :context="context" 
                :animated="false"
                :thumbnail="thumbnail" 
                :invert="invert" 
                :overflow="overflow" 
                :selectable="
                    selectable &&
                    (
                        !selectionLimitReached ||
                        isAssetSelected(asset)
                    )
                " 
                :persist="persist"
                @select="handleSelect" 
                @share="handleShare" 
                @download="handleDownload"
                @delete="handleDelete" 
            />
        </div>

    </div>
</template>

<script>
import AssetItem from '@/components/common/asset/AssetItem'

export default {
    name: 'AssetList',
    components: {
        AssetItem
    },
    props: {
        // list of assets
        list: {
            type: Array,
            default: null
        },

        // file key 
        file: {
            type: String,
            default: 'default'
        },

        // if true, context menu is available
        context: {
            type: Boolean,
            default: false  
        },

        // if true, item uses thumbnail instead of actual file (for faster loading)
        thumbnail: {
            type: Boolean,
            default: true
        },

        scaling: {
            type: Number,
            default: 1.0
        },

        // invert preview of asset
        invert: {
            type: Boolean,
            default: false
        },

        // if true, item is allowed to overflow border on hover
        overflow: {
            type: Boolean,
            default: false
        },

        // if true, select event is triggered on item click
        selectable: {
            type: Boolean,
            default: true
        },

        // if true, track selected items
        persist: {
            type: Boolean,
            default: false
        },

        // if true, multiple selection is allowed (only useful with selectable)
        multiple: {
            type: Boolean,
            default: false
        },

        maxAssets: {
            type: Number,
            default: null
        },

        // if true, limit list to visible items
        limit: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            itemSize: 150,
            visibleColumns: 1,
            visibleRows: 1,
            additionalRows: 0,
            selected: []
        }
    },
    computed: {
        limitCurrent() {
            return (this.visibleRows + this.additionalRows) * this.visibleColumns
        },
        limitedList() {
            if (!this.list) return []
            if (this.limit) {
                return this.list.slice(0, this.limitCurrent)
            }
            return this.list
        },
        gapSize() {
            if (this.itemSize < 200) return Math.floor(this.itemSize * 0.05)
            if (this.itemSize < 300) return Math.floor(this.itemSize * 0.15)
            return Math.floor(this.itemSize * 0.2)
        },
        totalSize() {
            return this.itemSize + 30
        },
        selectionLimitReached() {
            if (!this.persist || !this.multiple || this.maxAssets === null) {
                return false
            }

            return this.selected.length >= this.maxAssets
        }
    },
    emits: ['select', 'rate', 'edit', 'share', 'download', 'delete'],
    methods: {
        isAssetSelected(asset) {
            return this.selected.some(selected => selected.id === asset.id)
        },

        resetRows() {
            this.additionalRows = 0
        },
        addRow() {
            if (this.list && this.list.length > this.limitCurrent) {
                this.additionalRows += 1
            }
        },

        select(asset) {
            if (this.selected.includes(asset)) return
            this.selected.push(asset)
        },
        unselect(asset) {
            this.selected = this.selected.filter(a => a.id !== asset.id)
        },
        unselectAll(ignore=null) {
            const items = this.$refs.assetItems || []
            for (const item of items) {
                if (item.asset.id !== ignore.id) {
                    item?.unselect?.()
                }
            }
            this.selected = []
        },

        handleSelect(asset, selection) {
            if (this.persist) {
                if (selection) {
                    if (!this.multiple) this.unselectAll(asset)
                    this.select(asset)
                }
                else this.unselect(asset)
            }
            this.$emit('select', asset, this.selected)
        },
        handleShare(asset) {
            this.$emit('share', asset)
        },
        handleDownload(asset) {
            this.$emit('download', asset)
        },
        handleDelete(asset) {
            this.$emit('delete', asset)
        },
        handleResize() {
            const assetList = this.$refs.assetList
            const width = assetList?.clientWidth || 0
            const height = window.innerHeight || 0

            if (width <= 0 || height <= 0) return

            // Adjust item size based on width and thumbnail preference
            if (this.thumbnail) {
                this.itemSize = width > 1024 ? 200 : 150
            } else {
                if (width > 1440) this.itemSize = 400
                else if (width > 1280) this.itemSize = 350
                else if (width > 1024) this.itemSize = 280
                else if (width > 768) this.itemSize = 200
                else this.itemSize = 150
            }

            // apply scaling
            this.itemSize *= this.scaling

            if (width) this.visibleColumns = Math.floor(width / this.totalSize)
            if (height) this.visibleRows = Math.floor(height / this.totalSize) + 1
        }
    },
    mounted() {
        this.$nextTick(this.handleResize)
        window.addEventListener('resize', this.handleResize)
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize)
    }
}
</script>

<style scoped>
.asset-list {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-wrap: wrap;
    gap: 30px;
    width: 100%;
    height: 100%;
    padding-top: 30px;
    padding-bottom: 30px;
}

.asset-list-empty {
    color: rgb(var(--v-theme-primary));
    font-size: 24px;
}

.asset-list-item {
    cursor: pointer;
}

@media (max-width: 750px) {
    .asset-list-empty {
        font-size: 20px;
    }
}
</style>
